import type { PropsWithChildren } from 'react';
import { StyleSheet, View, type StyleProp, type ViewStyle } from 'react-native';

import { colors, radii, strokes, tint } from '../tokens';

export interface CardProps extends PropsWithChildren {
  /**
   * plain = white with a soft shadow (P02 auth card) · quiet = 1.5 #D6DEE6 (read inbox rows) · playful = 2 #1D2342 ·
   * playfulBold = 2.5 #1D2342 (hero cards: P10 empty, 1a approvals/kid cards) · playfulSlate = 2 #434C58 (P07 rows,
   * unread inbox rows, P03 toggle card) · magenta = 2 #D94991 (reward-request rows only).
   */
  variant?: 'plain' | 'quiet' | 'playful' | 'playfulBold' | 'playfulSlate' | 'magenta';
  /** row = 18 (P07/P09/C19 rows) · card = 20 · tile = 22 (C14 quiz tile) · cardLg = 24 · sheet = 28 (C13 story card, C05 hero). */
  radius?: 'row' | 'card' | 'tile' | 'cardLg' | 'sheet';
  padding?: number;
  /**
   * Hard plate depth "0 Npx 0 0 #170909" from `shadows` — the child surfaces draw cards on one (C13 story
   * card and C05 hero at `shadows.xl`). 0 (the default) leaves the card flat, as every parent card is.
   */
  plate?: number;
  style?: StyleProp<ViewStyle>;
}

export function Card({
  variant = 'quiet',
  radius = 'card',
  padding = 16,
  plate = 0,
  style,
  children,
}: CardProps) {
  return (
    <View
      style={[
        styles.base,
        { borderRadius: radii[radius], padding },
        styles[variant],
        plate > 0 ? { boxShadow: `0 ${plate}px 0 0 ${colors.ctaBorder}` } : null,
        style,
      ]}
    >
      {children}
    </View>
  );
}

const styles = StyleSheet.create({
  base: { backgroundColor: colors.white },
  // RN 0.86 and react-native-web both take CSS boxShadow; the colour comes from the derived-tint token.
  plain: { boxShadow: `0 6px 16px ${tint.ink12}`, borderWidth: 0 },
  quiet: { borderWidth: strokes.quiet, borderColor: colors.quietBorder },
  playful: { borderWidth: strokes.playful, borderColor: colors.ink },
  playfulBold: { borderWidth: strokes.playfulBold, borderColor: colors.ink },
  playfulSlate: { borderWidth: strokes.playful, borderColor: colors.slate },
  magenta: { borderWidth: strokes.playful, borderColor: colors.magenta },
});
